import {
  ComponentMetadata,
  GeneratedProperties,
  MetadataJsonContentType,
  PageBuilderProperties,
} from '../registry'

/**
 * There are some scenarios in Horizon Foundation where this will not match the package name
 * due to the generate-metadata script inferring the packageName from directory name where it doesn't match.
 */
export const findComponentMetadataFromJson = (
  packageName: string,
  componentName: string,
  metadataJsonContent?: MetadataJsonContentType,
) => {
  const exactMatch = metadataJsonContent?.find(
    (jsonComponentMetadata: { packageName: string; name: string }) =>
      jsonComponentMetadata.packageName === packageName &&
      jsonComponentMetadata.name === componentName,
  )

  if (exactMatch) return exactMatch

  const nameMatch = metadataJsonContent?.filter(
    (jsonComponentMetadata: { name: string }) =>
      jsonComponentMetadata.name === componentName,
  )

  if (nameMatch?.length === 1) return nameMatch[0]

  return null
}

/**
 * Merges exampleValue from generatedProps into user-defined metadata properties.
 * @param pageBuilderProperties - The metadata properties explicitly passed in as an object, not coming from the metadata.json file.
 * @param generatedProperties - The generated metadata properties.
 * @returns The merged properties object.
 */
export const mergeGeneratedExampleValuesIntoProperties = (
  pageBuilderProperties?: PageBuilderProperties,
  generatedProperties?: GeneratedProperties,
) => {
  if (!generatedProperties) return pageBuilderProperties
  if (!pageBuilderProperties) return generatedProperties

  return Object.fromEntries(
    Object.entries(pageBuilderProperties).map(([fieldName, prop]) => {
      const exampleValue = generatedProperties[fieldName]?.exampleValue
      return [fieldName, exampleValue ? { ...prop, exampleValue } : prop]
    }),
  )
}

export const mergeMetadataWithJsDocMetadata = (
  packageName: string,
  name: string,
  metadata?: ComponentMetadata & { jsDocTags?: Record<string, unknown> },
  metadataJsonContent?: MetadataJsonContentType,
) => {
  const generatedMetadata = findComponentMetadataFromJson(
    packageName,
    name,
    metadataJsonContent,
  )

  const jsDocTags = Object.assign(
    generatedMetadata?.jsDocTags || {},
    metadata?.jsDocTags || {},
  )

  if (metadata) {
    return {
      docsOnly: false,
      ...metadata,
      jsDocTags,
      properties: mergeGeneratedExampleValuesIntoProperties(
        metadata.properties as PageBuilderProperties,
        generatedMetadata?.properties as GeneratedProperties,
      ),
    }
  }

  if (generatedMetadata) {
    return { docsOnly: true, ...generatedMetadata }
  }

  return null
}
